// Compliance Calendar - SMSF compliance calendar and reminder system
// Manages deadlines, reminders, and compliance tracking for SMSF operations
// Last updated: January 2025

export const ComplianceCalendar = {
    // Compliance Deadline Manager
    DeadlineManager: {
        // Get all compliance deadlines for a financial year
        getComplianceDeadlines(financialYear = null) {
            const currentYear = financialYear || new Date().getFullYear();
            const nextYear = currentYear + 1;
            
            return {
                financialYear: `${currentYear}/${nextYear}`,
                deadlines: [
                    {
                        id: 'financial-statements',
                        category: 'Financial Reporting',
                        task: 'Prepare Financial Statements',
                        dueDate: new Date(nextYear, 9, 31), // 31 October
                        priority: 'High',
                        description: 'Annual financial statements must be prepared',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 1],
                        completed: false,
                        completionDate: null
                    },
                    {
                        id: 'audit-report',
                        category: 'Financial Reporting',
                        task: 'Annual Audit by Approved Auditor',
                        dueDate: new Date(nextYear, 9, 31), // 31 October
                        priority: 'High',
                        description: 'SMSF must be audited by an approved SMSF auditor',
                        responsible: 'Approved SMSF Auditor',
                        reminderDays: [45, 30, 14, 7, 1],
                        completed: false,
                        completionDate: null
                    },
                    {
                        id: 'annual-return',
                        category: 'ATO Reporting',
                        task: 'Lodge SMSF Annual Return',
                        dueDate: new Date(nextYear, 9, 31), // 31 October
                        priority: 'Critical',
                        description: 'SMSF Annual Return must be lodged with ATO',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 3, 1],
                        completed: false,
                        completionDate: null
                    },
                    {
                        id: 'member-statements',
                        category: 'Member Reporting',
                        task: 'Prepare Member Benefit Statements',
                        dueDate: new Date(nextYear, 9, 31), // 31 October
                        priority: 'High',
                        description: 'Annual member benefit statements must be prepared',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 1],
                        completed: false,
                        completionDate: null
                    },
                    {
                        id: 'contribution-reporting',
                        category: 'ATO Reporting',
                        task: 'Report Contributions to ATO',
                        dueDate: new Date(nextYear, 9, 28), // 28 October
                        priority: 'High',
                        description: 'Member contributions must be reported to ATO',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 1],
                        completed: false,
                        completionDate: null
                    },
                    {
                        id: 'quarterly-meetings',
                        category: 'Trustee Obligations',
                        task: 'Quarterly Trustee Meetings',
                        dueDate: this.getNextQuarterlyMeeting(),
                        priority: 'Medium',
                        description: 'Trustees must meet at least quarterly',
                        responsible: 'All Trustees',
                        reminderDays: [14, 7, 1],
                        completed: false,
                        completionDate: null,
                        recurring: true,
                        frequency: 'quarterly'
                    },
                    {
                        id: 'investment-strategy-review',
                        category: 'Investment Compliance',
                        task: 'Review Investment Strategy',
                        dueDate: new Date(currentYear, 5, 30), // 30 June
                        priority: 'Medium',
                        description: 'Investment strategy must be reviewed annually',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 1],
                        completed: false,
                        completionDate: null,
                        recurring: true,
                        frequency: 'annually'
                    },
                    {
                        id: 'insurance-review',
                        category: 'Member Benefits',
                        task: 'Review Insurance Arrangements',
                        dueDate: new Date(currentYear, 5, 30), // 30 June
                        priority: 'Medium',
                        description: 'Insurance arrangements should be reviewed annually',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7],
                        completed: false,
                        completionDate: null,
                        recurring: true,
                        frequency: 'annually'
                    },
                    {
                        id: 'member-nominations',
                        category: 'Member Benefits',
                        task: 'Update Death Benefit Nominations',
                        dueDate: new Date(currentYear, 5, 30), // 30 June
                        priority: 'Low',
                        description: 'Review and update death benefit nominations',
                        responsible: 'Members',
                        reminderDays: [30, 14],
                        completed: false,
                        completionDate: null,
                        recurring: true,
                        frequency: 'annually'
                    },
                    {
                        id: 'in-house-assets-check',
                        category: 'Investment Compliance',
                        task: 'Check In-House Asset Limits',
                        dueDate: new Date(currentYear, 5, 30), // 30 June
                        priority: 'High',
                        description: 'Verify in-house assets do not exceed 5% limit',
                        responsible: 'Trustees',
                        reminderDays: [30, 14, 7, 1],
                        completed: false,
                        completionDate: null,
                        recurring: true,
                        frequency: 'annually'
                    }
                ]
            };
        },

        // Get next quarterly meeting date
        getNextQuarterlyMeeting() {
            const now = new Date();
            const currentQuarter = Math.floor(now.getMonth() / 3);
            const nextQuarter = (currentQuarter + 1) % 4;
            const year = nextQuarter === 0 ? now.getFullYear() + 1 : now.getFullYear();
            const month = nextQuarter * 3;
            
            return new Date(year, month, 30); // End of next quarter
        },

        // Get overdue deadlines
        getOverdueDeadlines(deadlines) {
            const now = new Date();
            return deadlines.filter(deadline => 
                !deadline.completed && deadline.dueDate < now
            );
        },

        // Get upcoming deadlines
        getUpcomingDeadlines(deadlines, days = 30) {
            const now = new Date();
            const futureDate = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000));
            
            return deadlines.filter(deadline => 
                !deadline.completed && 
                deadline.dueDate >= now && 
                deadline.dueDate <= futureDate
            );
        },

        // Mark deadline as completed
        markDeadlineCompleted(deadlineId, completionDate = new Date()) {
            // This would update the deadline in storage
            return {
                success: true,
                deadlineId,
                completionDate: completionDate.toISOString()
            };
        }
    },

    // Reminder System
    ReminderSystem: {
        // Generate reminders for upcoming deadlines
        generateReminders(deadlines) {
            const reminders = [];
            const now = new Date();

            deadlines.forEach(deadline => {
                if (deadline.completed) return;

                deadline.reminderDays.forEach(days => {
                    const reminderDate = new Date(deadline.dueDate.getTime() - (days * 24 * 60 * 60 * 1000));
                    
                    if (reminderDate <= now && reminderDate >= new Date(now.getTime() - (24 * 60 * 60 * 1000))) {
                        reminders.push({
                            id: `${deadline.id}-${days}`,
                            deadlineId: deadline.id,
                            type: 'deadline',
                            title: `${deadline.task} - Due in ${days} day${days > 1 ? 's' : ''}`,
                            message: this.generateReminderMessage(deadline, days),
                            priority: this.getReminderPriority(deadline, days),
                            dueDate: deadline.dueDate,
                            reminderDate,
                            category: deadline.category,
                            responsible: deadline.responsible,
                            actions: this.getReminderActions(deadline)
                        });
                    }
                });
            });

            return reminders.sort((a, b) => a.dueDate - b.dueDate);
        },

        // Generate reminder message
        generateReminderMessage(deadline, daysRemaining) {
            const urgency = daysRemaining <= 7 ? 'urgent' : daysRemaining <= 14 ? 'important' : 'upcoming';
            
            const messages = {
                urgent: `⚠️ URGENT: ${deadline.task} is due in ${daysRemaining} day${daysRemaining > 1 ? 's' : ''} (${deadline.dueDate.toLocaleDateString()}). ${deadline.description}`,
                important: `📅 IMPORTANT: ${deadline.task} is due in ${daysRemaining} days (${deadline.dueDate.toLocaleDateString()}). ${deadline.description}`,
                upcoming: `📋 REMINDER: ${deadline.task} is due in ${daysRemaining} days (${deadline.dueDate.toLocaleDateString()}). ${deadline.description}`
            };

            return messages[urgency];
        },

        // Get reminder priority
        getReminderPriority(deadline, daysRemaining) {
            if (daysRemaining <= 3) return 'critical';
            if (daysRemaining <= 7) return 'high';
            if (daysRemaining <= 14) return 'medium';
            return 'low';
        },

        // Get reminder actions
        getReminderActions(deadline) {
            const actions = [
                {
                    id: 'view-details',
                    label: 'View Details',
                    type: 'info'
                },
                {
                    id: 'mark-complete',
                    label: 'Mark Complete',
                    type: 'success'
                }
            ];

            if (deadline.category === 'ATO Reporting') {
                actions.push({
                    id: 'generate-report',
                    label: 'Generate Report',
                    type: 'primary'
                });
            }

            if (deadline.category === 'Trustee Obligations') {
                actions.push({
                    id: 'schedule-meeting',
                    label: 'Schedule Meeting',
                    type: 'secondary'
                });
            }

            return actions;
        },

        // Schedule recurring reminders
        scheduleRecurringReminders(deadlines) {
            const recurringDeadlines = deadlines.filter(d => d.recurring);
            const scheduledReminders = [];

            recurringDeadlines.forEach(deadline => {
                const nextOccurrence = this.getNextRecurringDate(deadline);
                if (nextOccurrence) {
                    scheduledReminders.push({
                        ...deadline,
                        dueDate: nextOccurrence,
                        id: `${deadline.id}-${nextOccurrence.getFullYear()}`
                    });
                }
            });

            return scheduledReminders;
        },

        // Get next recurring date
        getNextRecurringDate(deadline) {
            const now = new Date();
            
            if (deadline.frequency === 'annually') {
                const nextYear = now.getFullYear() + 1;
                return new Date(deadline.dueDate.getTime() + (365 * 24 * 60 * 60 * 1000));
            }
            
            if (deadline.frequency === 'quarterly') {
                const nextQuarter = new Date(now);
                nextQuarter.setMonth(nextQuarter.getMonth() + 3);
                return nextQuarter;
            }
            
            return null;
        }
    },

    // Compliance Dashboard
    ComplianceDashboard: {
        // Generate compliance dashboard data
        generateDashboardData(smsfData) {
            const deadlines = this.DeadlineManager.getComplianceDeadlines();
            const reminders = this.ReminderSystem.generateReminders(deadlines.deadlines);
            
            return {
                summary: this.generateSummary(deadlines.deadlines, reminders),
                upcoming: this.getUpcomingDeadlines(deadlines.deadlines, 30),
                overdue: this.getOverdueDeadlines(deadlines.deadlines),
                reminders: reminders.slice(0, 5), // Next 5 reminders
                complianceScore: this.calculateComplianceScore(deadlines.deadlines),
                categories: this.groupByCategory(deadlines.deadlines)
            };
        },

        // Generate summary statistics
        generateSummary(deadlines, reminders) {
            const total = deadlines.length;
            const completed = deadlines.filter(d => d.completed).length;
            const overdue = deadlines.filter(d => !d.completed && d.dueDate < new Date()).length;
            const upcoming = deadlines.filter(d => !d.completed && d.dueDate >= new Date()).length;
            const activeReminders = reminders.filter(r => r.priority === 'critical' || r.priority === 'high').length;

            return {
                total,
                completed,
                overdue,
                upcoming,
                activeReminders,
                completionRate: total > 0 ? Math.round((completed / total) * 100) : 100
            };
        },

        // Get upcoming deadlines
        getUpcomingDeadlines(deadlines, days = 30) {
            const now = new Date();
            const futureDate = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000));
            
            return deadlines
                .filter(deadline => 
                    !deadline.completed && 
                    deadline.dueDate >= now && 
                    deadline.dueDate <= futureDate
                )
                .sort((a, b) => a.dueDate - b.dueDate);
        },

        // Get overdue deadlines
        getOverdueDeadlines(deadlines) {
            const now = new Date();
            return deadlines
                .filter(deadline => !deadline.completed && deadline.dueDate < now)
                .sort((a, b) => a.dueDate - b.dueDate);
        },

        // Calculate compliance score
        calculateComplianceScore(deadlines) {
            const total = deadlines.length;
            const completed = deadlines.filter(d => d.completed).length;
            const overdue = deadlines.filter(d => !d.completed && d.dueDate < new Date()).length;
            
            let score = (completed / total) * 100;
            
            // Penalty for overdue items
            if (overdue > 0) {
                score -= (overdue / total) * 50;
            }
            
            return Math.max(0, Math.round(score));
        },

        // Group deadlines by category
        groupByCategory(deadlines) {
            const categories = {};
            
            deadlines.forEach(deadline => {
                if (!categories[deadline.category]) {
                    categories[deadline.category] = {
                        name: deadline.category,
                        total: 0,
                        completed: 0,
                        overdue: 0,
                        upcoming: 0
                    };
                }
                
                categories[deadline.category].total++;
                
                if (deadline.completed) {
                    categories[deadline.category].completed++;
                } else if (deadline.dueDate < new Date()) {
                    categories[deadline.category].overdue++;
                } else {
                    categories[deadline.category].upcoming++;
                }
            });
            
            return Object.values(categories);
        }
    },

    // Integration with Push Notifications
    NotificationIntegration: {
        // Send compliance reminders via push notifications
        async sendComplianceReminders(reminders) {
            const criticalReminders = reminders.filter(r => r.priority === 'critical');
            const highPriorityReminders = reminders.filter(r => r.priority === 'high');
            
            for (const reminder of criticalReminders) {
                await this.sendCriticalReminder(reminder);
            }
            
            for (const reminder of highPriorityReminders) {
                await this.sendHighPriorityReminder(reminder);
            }
        },

        // Send critical reminder
        async sendCriticalReminder(reminder) {
            // This would integrate with the PushNotificationService
            return {
                success: true,
                reminderId: reminder.id,
                sent: true,
                method: 'push_notification'
            };
        },

        // Send high priority reminder
        async sendHighPriorityReminder(reminder) {
            // This would integrate with the PushNotificationService
            return {
                success: true,
                reminderId: reminder.id,
                sent: true,
                method: 'push_notification'
            };
        }
    },

    // Main calendar management function
    getComplianceCalendar(financialYear = null, smsfData = null) {
        const deadlines = this.DeadlineManager.getComplianceDeadlines(financialYear);
        const reminders = this.ReminderSystem.generateReminders(deadlines.deadlines);
        const dashboard = this.ComplianceDashboard.generateDashboardData(smsfData);
        
        return {
            financialYear: deadlines.financialYear,
            deadlines: deadlines.deadlines,
            reminders,
            dashboard,
            generatedDate: new Date().toISOString()
        };
    },

    // Export calendar data
    exportCalendarData(calendarData, format = 'json') {
        switch (format) {
            case 'json':
                return JSON.stringify(calendarData, null, 2);
            case 'csv':
                return this.exportToCSV(calendarData);
            case 'ical':
                return this.exportToICS(calendarData);
            default:
                throw new Error(`Unsupported export format: ${format}`);
        }
    },

    // Export to CSV
    exportToCSV(calendarData) {
        const headers = ['Task', 'Category', 'Due Date', 'Priority', 'Responsible', 'Completed', 'Description'];
        const rows = calendarData.deadlines.map(deadline => [
            deadline.task,
            deadline.category,
            deadline.dueDate.toLocaleDateString(),
            deadline.priority,
            deadline.responsible,
            deadline.completed ? 'Yes' : 'No',
            deadline.description
        ]);
        
        return [headers, ...rows].map(row => row.join(',')).join('\n');
    },

    // Export to ICS (iCalendar format)
    exportToICS(calendarData) {
        let ics = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//SMSF Calculator//Compliance Calendar//EN\n';
        
        calendarData.deadlines.forEach(deadline => {
            const startDate = deadline.dueDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            const endDate = new Date(deadline.dueDate.getTime() + (24 * 60 * 60 * 1000)).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            
            ics += `BEGIN:VEVENT\n`;
            ics += `UID:${deadline.id}@smsfcalculator.com\n`;
            ics += `DTSTART:${startDate}\n`;
            ics += `DTEND:${endDate}\n`;
            ics += `SUMMARY:${deadline.task}\n`;
            ics += `DESCRIPTION:${deadline.description}\n`;
            ics += `CATEGORIES:${deadline.category}\n`;
            ics += `PRIORITY:${deadline.priority === 'Critical' ? '1' : deadline.priority === 'High' ? '2' : '3'}\n`;
            ics += `STATUS:${deadline.completed ? 'CONFIRMED' : 'TENTATIVE'}\n`;
            ics += `END:VEVENT\n`;
        });
        
        ics += 'END:VCALENDAR';
        return ics;
    }
};

export default ComplianceCalendar;
